import { UpdateTenantRequest } from '@stayafrica/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Container,
  ErrorState,
  FormField,
  Input,
  NoPermissionState,
  Notice,
  PageHeader,
  Skeleton,
  Tab,
  TabPanel,
  Table,
  TableWrap,
  Tabs,
  TabsList,
  Td,
  Th,
  Tr,
  toast,
} from '@stayafrica/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  BedDouble,
  Building2,
  Clock,
  CreditCard,
  Landmark,
  Palette,
  Receipt,
  Scale,
  ShieldCheck,
} from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useCan } from '@/features/auth/session';
import { BillingSettingsTab } from '@/features/billing/BillingSettingsTab';
import { ComplianceRequirementsTab } from '@/features/compliance/ComplianceRequirementsTab';
import { ResourceCatalogTab } from '@/features/resources/ResourceCatalogTab';
import {
  propertiesQueryOptions,
  tenantKeys,
  tenantQueryOptions,
  updateTenant,
} from '@/features/tenant/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatPhone } from '@/lib/format';

export const Route = createFileRoute('/_app/admin/settings')({ component: SettingsPage });

interface TenantFormValues {
  legalName: string;
  tradeName: string;
  rccm: string;
  ifu: string;
  taxRegime: string;
  taxDivision: string;
  legalForm: string;
  email: string;
  phone: string;
  website: string;
  line1: string;
  line2: string;
  poBox: string;
  city: string;
  bankName: string;
  accountNumber: string;
  documentSignatory: string;
  primaryColor: string;
  secondaryColor: string;
}

function SettingsPage() {
  const { t } = useTranslation(['settings', 'compliance', 'common', 'errors']);
  const canRead = useCan('tenant.read');
  const canUpdate = useCan('tenant.update');
  const queryClient = useQueryClient();

  const tenantQuery = useQuery({ ...tenantQueryOptions(), enabled: canRead });
  const propertiesQuery = useQuery({ ...propertiesQueryOptions(), enabled: canRead });

  const form = useForm<TenantFormValues>({ mode: 'onBlur' });

  useEffect(() => {
    const tenant = tenantQuery.data;
    if (!tenant) return;
    form.reset({
      legalName: tenant.legalName,
      tradeName: tenant.tradeName ?? '',
      rccm: tenant.legal.rccm ?? '',
      ifu: tenant.legal.ifu ?? '',
      taxRegime: tenant.legal.taxRegime ?? '',
      taxDivision: tenant.legal.taxDivision ?? '',
      legalForm: tenant.legal.legalForm ?? '',
      email: tenant.contact.email ?? '',
      phone: tenant.contact.phone ?? '',
      website: tenant.contact.website ?? '',
      line1: tenant.contact.address?.line1 ?? '',
      line2: tenant.contact.address?.line2 ?? '',
      poBox: tenant.contact.address?.poBox ?? '',
      city: tenant.contact.address?.city ?? '',
      bankName: tenant.bankDetails.bankName ?? '',
      accountNumber: tenant.bankDetails.accountNumber ?? '',
      documentSignatory: tenant.branding.documentSignatory ?? '',
      primaryColor: tenant.branding.primaryColor ?? '#5F24CF',
      secondaryColor: tenant.branding.secondaryColor ?? '#0E7AC1',
    });
  }, [tenantQuery.data, form]);

  const mutation = useMutation({
    mutationFn: (values: TenantFormValues) => {
      const tenant = tenantQuery.data;
      if (!tenant) throw new Error('Tenant indisponible');
      const payload: z.infer<typeof UpdateTenantRequest> = {
        version: tenant.version,
        legalName: values.legalName,
        tradeName: values.tradeName || null,
        legal: {
          rccm: values.rccm || null,
          ifu: values.ifu || null,
          taxRegime: values.taxRegime || null,
          taxDivision: values.taxDivision || null,
          legalForm: values.legalForm || null,
        },
        contact: {
          email: values.email || null,
          phone: values.phone || null,
          website: values.website || null,
          address: {
            line1: values.line1 || null,
            line2: values.line2 || null,
            poBox: values.poBox || null,
            city: values.city || null,
            countryCode: tenant.countryCode,
          },
        },
        bankDetails: {
          bankName: values.bankName || null,
          accountNumber: values.accountNumber || null,
          iban: tenant.bankDetails.iban,
          swift: tenant.bankDetails.swift,
        },
        branding: {
          logoUrl: tenant.branding.logoUrl,
          primaryColor: values.primaryColor || null,
          secondaryColor: values.secondaryColor || null,
          documentSignatory: values.documentSignatory || null,
        },
      };
      return updateTenant(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tenantKeys.tenant });
      toast.success(t('settings:toasts.tenantUpdated'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  if (tenantQuery.isPending) {
    return (
      <Container>
        <div className="stack" aria-busy="true">
          <Skeleton variant="title" />
          <Skeleton height={280} />
        </div>
      </Container>
    );
  }

  if (tenantQuery.isError || !tenantQuery.data) {
    return (
      <Container>
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(tenantQuery.error, t)}
          traceId={errorTraceId(tenantQuery.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void tenantQuery.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      </Container>
    );
  }

  const tenant = tenantQuery.data;
  const properties = propertiesQuery.data ?? [];

  const field = (name: keyof TenantFormValues, label: string, hint?: string) => (
    <FormField label={label} {...(hint ? { hint } : {})}>
      {(control) => (
        <Input
          id={control.id}
          disabled={!canUpdate}
          aria-describedby={control.describedBy}
          {...form.register(name)}
        />
      )}
    </FormField>
  );

  return (
    <Container>
      <PageHeader
        title={t('settings:title')}
        subtitle={t('settings:subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('settings:title') },
        ]}
        actions={
          canUpdate ? (
            <Button
              variant="primary"
              loading={mutation.isPending}
              onClick={() => void form.handleSubmit((values) => mutation.mutate(values))()}
            >
              {t('common:actions.save')}
            </Button>
          ) : null
        }
      />

      {!canUpdate ? (
        <div className="mb-4">
          <Notice tone="info">{t('settings:readOnlyNotice')}</Notice>
        </div>
      ) : null}

      <Tabs defaultValue="identity">
        <TabsList>
          <Tab value="identity" icon={<Building2 className="ico" aria-hidden="true" />}>
            {t('settings:tabs.identity')}
          </Tab>
          <Tab value="legal" icon={<Scale className="ico" aria-hidden="true" />}>
            {t('settings:tabs.legal')}
          </Tab>
          <Tab value="bank" icon={<Landmark className="ico" aria-hidden="true" />}>
            {t('settings:tabs.bank')}
          </Tab>
          <Tab value="branding" icon={<Palette className="ico" aria-hidden="true" />}>
            {t('settings:tabs.branding')}
          </Tab>
          <Tab value="properties" icon={<Clock className="ico" aria-hidden="true" />}>
            {t('settings:tabs.properties')}
          </Tab>
          <Tab value="resourceCatalog" icon={<BedDouble className="ico" aria-hidden="true" />}>
            {t('settings:tabs.resourceCatalog')}
          </Tab>
          <Tab value="compliance" icon={<ShieldCheck className="ico" aria-hidden="true" />}>
            {t('settings:tabs.compliance')}
          </Tab>
          <Tab value="billing" icon={<Receipt className="ico" aria-hidden="true" />}>
            {t('settings:tabs.billing')}
          </Tab>
        </TabsList>

        <TabPanel value="identity">
          <Card>
            <CardHeader
              title={t('settings:identity.card')}
              icon={<Building2 className="ico ico-16" aria-hidden="true" />}
            />
            <CardBody>
              <div className="stack">
                <div className="field-row">
                  {field('legalName', t('settings:identity.legalName'))}
                  {field('tradeName', t('settings:identity.tradeName'))}
                </div>
                <div className="field-row-3">
                  <FormField label={t('settings:identity.country')}>
                    {(control) => (
                      <Input id={control.id} value={tenant.countryCode} disabled readOnly />
                    )}
                  </FormField>
                  <FormField label={t('settings:identity.currency')}>
                    {(control) => (
                      <Input id={control.id} value={tenant.currencyCode} disabled readOnly />
                    )}
                  </FormField>
                  <FormField label={t('settings:identity.timezone')}>
                    {(control) => (
                      <Input id={control.id} value={tenant.timezone} disabled readOnly />
                    )}
                  </FormField>
                </div>
                <div className="field-row-3">
                  {field('email', t('settings:identity.email'))}
                  {field('phone', t('settings:identity.phone'), formatPhone(tenant.contact.phone))}
                  {field('website', t('settings:identity.website'))}
                </div>
                <div className="field-row">
                  {field('line1', t('settings:identity.addressLine1'))}
                  {field('line2', t('settings:identity.addressLine2'))}
                </div>
                <div className="field-row">
                  {field('poBox', t('settings:identity.poBox'))}
                  {field('city', t('settings:identity.city'))}
                </div>
              </div>
            </CardBody>
          </Card>
        </TabPanel>

        <TabPanel value="legal">
          <Card>
            <CardHeader
              title={t('settings:legal.card')}
              icon={<Scale className="ico ico-16" aria-hidden="true" />}
              iconTone="amber"
            />
            <CardBody>
              <div className="stack">
                <Notice tone="info">{t('settings:legal.hint')}</Notice>
                <div className="field-row">
                  {field('rccm', t('settings:legal.rccm'))}
                  {field('ifu', t('settings:legal.ifu'))}
                </div>
                <div className="field-row-3">
                  {field('taxRegime', t('settings:legal.taxRegime'))}
                  {field('taxDivision', t('settings:legal.taxDivision'))}
                  {field('legalForm', t('settings:legal.legalForm'))}
                </div>
              </div>
            </CardBody>
          </Card>
        </TabPanel>

        <TabPanel value="bank">
          <Card>
            <CardHeader
              title={t('settings:bank.card')}
              icon={<CreditCard className="ico ico-16" aria-hidden="true" />}
              iconTone="green"
            />
            <CardBody>
              <div className="field-row">
                {field('bankName', t('settings:bank.bankName'))}
                {field('accountNumber', t('settings:bank.accountNumber'))}
              </div>
            </CardBody>
          </Card>
        </TabPanel>

        <TabPanel value="branding">
          <Card>
            <CardHeader
              title={t('settings:branding.card')}
              icon={<Palette className="ico ico-16" aria-hidden="true" />}
            />
            <CardBody>
              <div className="field-row-3">
                {field('primaryColor', t('settings:branding.primaryColor'))}
                {field('secondaryColor', t('settings:branding.secondaryColor'))}
                {field('documentSignatory', t('settings:branding.documentSignatory'))}
              </div>
            </CardBody>
            <CardFooter>
              <span
                className="dev-swatch"
                style={{
                  background: tenant.branding.primaryColor ?? '#5F24CF',
                  color: '#fff',
                  width: 140,
                }}
              >
                {tenant.branding.primaryColor}
              </span>
              <span
                className="dev-swatch"
                style={{
                  background: tenant.branding.secondaryColor ?? '#0E7AC1',
                  color: '#fff',
                  width: 140,
                }}
              >
                {tenant.branding.secondaryColor}
              </span>
            </CardFooter>
          </Card>
        </TabPanel>

        <TabPanel value="properties">
          <Card>
            <CardHeader
              title={t('settings:properties.card')}
              icon={<Clock className="ico ico-16" aria-hidden="true" />}
              iconTone="blue"
            />
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>{t('settings:properties.name')}</Th>
                    <Th>{t('settings:properties.code')}</Th>
                    <Th>{t('settings:properties.checkinFrom')}</Th>
                    <Th>{t('settings:properties.checkoutUntil')}</Th>
                    <Th>{t('settings:properties.timezone')}</Th>
                    <Th>{t('common:fields.status')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {properties.map((property) => (
                    <Tr key={property.id}>
                      <Td strong>{property.name}</Td>
                      <Td className="cell-id">{property.code}</Td>
                      <Td numeric>{property.checkinFrom}</Td>
                      <Td numeric>{property.checkoutUntil}</Td>
                      <Td className="t-13">{property.timezone}</Td>
                      <Td>
                        <Badge tone={property.isActive ? 'green' : 'neutral'} dot>
                          {property.isActive
                            ? t('settings:properties.active')
                            : t('settings:properties.inactive')}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
            <CardFooter>
              <span className="hint">{t('settings:properties.hint')}</span>
            </CardFooter>
          </Card>
        </TabPanel>

        {/* Référentiel des types de ressources et catalogue de caractéristiques. */}
        <TabPanel value="resourceCatalog">
          <ResourceCatalogTab />
        </TabPanel>

        <TabPanel value="compliance">
          <ComplianceRequirementsTab />
        </TabPanel>

        <TabPanel value="billing">
          <BillingSettingsTab />
        </TabPanel>
      </Tabs>
    </Container>
  );
}
